import React, { useState } from "react";
import { Form, Input, Select, message } from "antd";
import styled from "styled-components";
import { campaignAPI, CampaignStatusPhase, CampaignType, getStatusTypeFromPhase } from "@/api/campaign";
import type { CampaignData } from "@/api/campaign";
import { DynamicModal } from "@/shared/components";
import { mediaPhone } from "@/shared/theme/breakpoints";
import { CURRENCY_OPTIONS } from "@/shared/utils/currency";
import { formatBudgetValue } from "@/shared/utils/budget";

interface Props {
	open: boolean;
	campaigns: CampaignData[];
	onClose: () => void;
	onCreated: (created: CampaignData) => void;
	onSearchDuplicate: (name: string) => void;
}

const FormContainer = styled(Form)`
	display: flex;
	flex-direction: column;
	gap: 16px;
	/* Matches the space the form had above its actions before it moved to DynamicModal. */
	margin-bottom: 20px;

	/* DynamicModal spaces form items itself; the gap above keeps this form's own rhythm. */
	&& .ant-form-item {
		margin-bottom: 0 !important;
	}
`;

const BudgetGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1.4fr) minmax(160px, 0.8fr);
	gap: 16px;

	${mediaPhone`grid-template-columns: minmax(0, 1fr);`}
`;

export const CreateCampaignModal: React.FC<Props> = ({ open, campaigns, onClose, onCreated, onSearchDuplicate }) => {
	const [form] = Form.useForm();
	const [formValid, setFormValid] = useState(false);
	const [creating, setCreating] = useState(false);
	const [dupModalOpen, setDupModalOpen] = useState(false);
	const [dupName, setDupName] = useState("");
	// Values to create from if the user chooses "Create Anyway" on the duplicate prompt.
	const [dupValues, setDupValues] = useState<Record<string, unknown> | null>(null);

	const close = () => {
		form.resetFields();
		setFormValid(false);
		onClose();
	};

	const doCreate = async (fv: any) => {
		try {
			setCreating(true);
			const normalizedTotalBudget = fv.totalBudget == null ? "" : String(fv.totalBudget).replace(/,/g, "").trim();
			const totalBudget = normalizedTotalBudget === "" ? undefined : Number(normalizedTotalBudget);
			const created = await campaignAPI.create({
				name: fv.campaignName,
				projectCode: fv.projectCode || "CREATORVERSE",
				status: getStatusTypeFromPhase(fv.status),
				statusPhase: fv.status,
				type: fv.type,
				amplifyTeam: [],
				lead: [],
				location: [],
				client: fv.client ? [fv.client] : [],
				lastModificationTime: new Date().toISOString(),
				totalBudget: Number.isFinite(totalBudget) ? totalBudget : undefined,
				currency: fv.currency || "USD",
			});
			message.success("Campaign created successfully!");
			close();
			onCreated(created);
		} catch {
			message.error("Failed to create campaign");
		} finally {
			setCreating(false);
		}
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			if (!values.projectCode?.trim()) {
				values.projectCode = "CREATORVERSE";
				form.setFieldsValue({ projectCode: "CREATORVERSE" });
			}
			const dup = campaigns.find(
				(c) =>
					c.name.toLowerCase() === values.campaignName.trim().toLowerCase() &&
					c.projectCode.toLowerCase() === values.projectCode.trim().toLowerCase(),
			);
			if (dup) {
				setDupName(dup.name);
				setDupValues(values);
				setDupModalOpen(true);
				return;
			}
			await doCreate(values);
		} catch (e) {
			console.error(e);
		}
	};

	return (
		<>
			<DynamicModal
				// The duplicate prompt replaces this sheet until the user picks an option.
				open={open && !dupModalOpen}
				onCancel={close}
				title="New Campaign"
				icon={{ src: "/icon/menu_campaign.svg", size: 36, color: "#454545" }}
				width={520}
				confirmText="Create"
				onConfirm={handleSubmit}
				confirmDisabled={!formValid}
				loading={creating}
				fullWidthActions
			>
				<FormContainer
					form={form}
					layout="vertical"
					initialValues={{ currency: "USD" }}
					onValuesChange={() => {
						const v = form.getFieldsValue();
						setFormValid(!!v.campaignName?.trim() && !!v.type && !!v.status);
					}}
				>
					<Form.Item
						name="campaignName"
						label="Campaign Name"
						rules={[{ required: true, message: "Please enter campaign name!" }]}
					>
						<Input />
					</Form.Item>
					<Form.Item name="projectCode" label="Project Code (Optional)">
						<Input />
					</Form.Item>
					<Form.Item name="client" label="Brand (Optional)">
						<Input />
					</Form.Item>
					<Form.Item name="type" label="Type" rules={[{ required: true, message: "Please select type!" }]}>
						<Select options={Object.values(CampaignType).map((t) => ({ label: t, value: t }))} />
					</Form.Item>
					<Form.Item
						name="status"
						label="Status"
						rules={[{ required: true, message: "Please select status!" }]}
					>
						<Select options={Object.values(CampaignStatusPhase).map((p) => ({ label: p, value: p }))} />
					</Form.Item>
					<BudgetGrid>
						<Form.Item
							name="totalBudget"
							label="Total Budget"
							normalize={(value: string) => formatBudgetValue(value)}
							rules={[
								{
									validator: async (_rule, value) => {
										if (value == null || value === "") {
											return;
										}

										const stripped = String(value).replace(/,/g, "");
										// allow integers or decimals with up to two fractional digits
										if (!/^\d+(?:\.\d{1,2})?$/.test(stripped)) {
											throw new Error("Budget must be numeric");
										}
									},
								},
							]}
						>
							<Input inputMode="numeric" placeholder="0" />
						</Form.Item>
						<Form.Item name="currency" label="Currency">
							<Select
								className="currency-select"
								showSearch
								options={CURRENCY_OPTIONS}
								placeholder="Select"
								// show symbol in the select value
								optionLabelProp="label"
								// allow searching by currency code
								optionFilterProp="value"
							/>
						</Form.Item>
					</BudgetGrid>
				</FormContainer>
			</DynamicModal>
			<DynamicModal
				open={dupModalOpen}
				// A decision prompt: the user either searches for the existing campaign or creates anyway.
				closable={false}
				onCancel={() => {
					setDupModalOpen(false);
					close();
					onSearchDuplicate(dupName);
				}}
				title="Duplicate Campaign Detected"
				description={
					<>
						A campaign with the same <strong>name</strong> and <strong>project code</strong> already exists.
						<br />
						<br />
						Do you still want to add it? Otherwise, please search for the existing campaign.
					</>
				}
				width={550}
				cancelText="Search Campaign"
				confirmText="Create Anyway"
				onConfirm={() => {
					setDupModalOpen(false);
					void doCreate(dupValues);
				}}
				fullWidthActions
			/>
		</>
	);
};
